import type { Key } from '@vizit/atoms';
import { useDebounce } from '@vizit/hooks';
import { ComboBox } from '@vizit/molecules';
import * as React from 'react';

type InputFieldState = {
  /**
   * Currently selected key
   *
   * @optional
   * @type {Key | null}
   */
  selectedKey?: Key | null;
  /**
   * Current input value
   *
   * @type {string}
   */
  inputValue: string;
};

type BrandItem = {
  id: string;
  name: string;
};

interface BrandComboBoxProps {
  /**
   * The default value for the combo box.
   */
  defaultValue?: Key | null;
  /**
   * The menu trigger for the combo box.
   */
  menuTrigger?: 'input' | 'focus';
  /**
   * The label for the combo box.
   */
  label?: string;
  /**
   * The class name for the combo box.
   */
  className?: string;
  /**
   * The filter text for the combo box.
   */
  filterText: string;
  /**
   * The brand items as a string array.
   */
  items: string[];
  /**
   * Whether the combo box is loading.
   */
  isLoading: boolean;
  /**
   * The selected key for the combo box.
   */
  selectedKey?: Key | null;
  /**
   * The function to set the filter text.
   */
  setFilterText: (text: string) => void;
  /**
   * The function to load more items.
   */
  loadMore: () => void;
  /**
   * The function to handle the selection change.
   */
  onSelectionChange?: (key: Key | null) => void;
}

/**
 * Brand combo box component with infinite scrolling and filtering capabilities.
 * This is an uncontrolled component that manages its own state internally.
 *
 * For form integration with react-hook-form, use FormBrandComboBox instead.
 *
 * @param props - Component props
 * @returns JSX element
 */
function BrandComboBox(props: BrandComboBoxProps) {
  const {
    defaultValue,
    items,
    isLoading,
    filterText,
    selectedKey,
    menuTrigger = 'focus',
    setFilterText,
    loadMore,
    onSelectionChange,
    ...other
  } = props;

  // Transform string array to brand items with unique keys
  const brandItems = React.useMemo(() => {
    const itemsMap = items.reduce((map, brand) => {
      const name = brand.trim();
      const id = name.toLowerCase().replace(' ', '-');

      if (!map.has(id)) {
        map.set(id, { id, name });
      }

      return map;
    }, new Map<string, BrandItem>());

    return Array.from(itemsMap.values());
  }, [items]);

  // default values
  const defaultSelectedKey = React.useMemo(
    () => (defaultValue || selectedKey ? defaultValue || selectedKey : undefined),
    [defaultValue, selectedKey],
  );
  const defaultInputValue = React.useMemo(
    () => (defaultSelectedKey ? brandItems.find((item) => item.id === defaultSelectedKey)?.name : ''),
    [defaultSelectedKey, brandItems],
  );

  const [inputFieldState, setInputFieldState] = React.useState<InputFieldState>({
    selectedKey: defaultSelectedKey || selectedKey || null,
    inputValue: defaultInputValue || filterText || '',
  });

  const [localFilterText, setLocalFilterText] = React.useState(filterText || '');
  const debouncedFilterText = useDebounce(localFilterText, 300);

  // Only update the parent's filterText when debounced value changes
  React.useEffect(() => {
    setFilterText(debouncedFilterText);
  }, [debouncedFilterText, setFilterText]);

  const handleOnSelectionChange = (key: Key | null) => {
    const inputValue = brandItems.find((item) => item.id === key)?.name || '';

    setInputFieldState({
      selectedKey: key,
      inputValue,
    });

    onSelectionChange?.(inputValue); // API uses the brand name not the key
  };

  const handleOnInputChange = (inputValue: string) => {
    setInputFieldState((prevState: InputFieldState) => ({
      inputValue,
      selectedKey: inputValue === '' ? null : prevState.selectedKey,
    }));

    setLocalFilterText(inputValue);
  };

  // Sync field value with input value
  React.useEffect(() => {
    if (!selectedKey) {
      setInputFieldState({
        selectedKey: null,
        inputValue: '',
      });

      setLocalFilterText('');
      setFilterText('');
    }
  }, [selectedKey, setFilterText]);

  const renderEmptyState = React.useCallback(() => {
    if (isLoading) return null;
    return <ComboBox.Empty>No brands found</ComboBox.Empty>;
  }, [isLoading]);

  return (
    <ComboBox<BrandItem>
      aria-label="Brand filter"
      isLoading={isLoading}
      inputValue={inputFieldState.inputValue}
      onInputChange={handleOnInputChange}
      onLoadMore={loadMore}
      items={brandItems}
      onSelectionChange={handleOnSelectionChange}
      selectedKey={inputFieldState.selectedKey}
      placeholder="All brands"
      menuTrigger={menuTrigger}
      renderEmptyState={renderEmptyState}
      popoverProps={{
        shouldFlip: false,
      }}
      {...other}
    >
      {(item: BrandItem) => (
        <ComboBox.Item key={item.id} textValue={item.name}>
          {item.name}
        </ComboBox.Item>
      )}
    </ComboBox>
  );
}

export { BrandComboBox };
export type { BrandComboBoxProps };
